import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Tooltip from '@mui/material/Tooltip';
import HelpIcon from '@material-ui/icons/HelpSharp';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTextInBold } from '~/modules/common/util';
import { ALLOCATION_PERIOD } from './AllocationPeriodTabs';

const useStyles = makeStyles(theme => ({
  warningContainer: {
    width: '90%',
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.warning.lightBackground,
    borderRadius: theme.shape.borderRadius
  },
  alert: {
    padding: 0,
    alignItems: 'center',
    '& .MuiAlert-message': {
      padding: 0
    }
  },
  helpIcon: {
    color: theme.palette.text.secondary,
    verticalAlign: 'middle'
  }
}));

const useTooltipStyles = makeStyles(() => ({
  tooltip: {
    maxWidth: '440px !important'
  }
}));

const getProrationLabels = (formatMessage, allocationPeriod) => {
  const messageId =
    allocationPeriod === ALLOCATION_PERIOD.MONTH
      ? 'allocationByPeriod.allocationPeriod.month'
      : 'allocationByPeriod.allocationPeriod.week';

  const periodLabel = formatMessage({ id: messageId }, { count: 1 });

  return {
    tooltipText: formatMessage(
      { id: 'allocationByPeriod.prorationTooltip' },
      { allocationPeriod: periodLabel }
    ),
    noteLabel: formatMessage({ id: messageId }, { count: 2 })
  };
};

const ProrationNoteId = 'proration-note-text';
const ProrationTooltipId = 'proration-note-tooltip';

const ProrationNote = ({ allocationPeriod }) => {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles();
  const { formatMessage } = useIntl();

  const { tooltipText, noteLabel } = getProrationLabels(
    formatMessage,
    allocationPeriod
  );

  return (
    <div className={classes.warningContainer}>
      <Alert severity="warning" icon={false} className={classes.alert}>
        <Typography variant="caption" id={ProrationNoteId}>
          <FormattedMessage
            id="allocationByPeriod.prorationNote"
            values={getTextInBold({ allocationPeriod: noteLabel })}
          />
        </Typography>
      </Alert>
      <Tooltip
        title={tooltipText}
        classes={tooltipClasses}
        enterTouchDelay={0}
        id={ProrationTooltipId}
      >
        <HelpIcon
          className={classes.helpIcon}
          tabIndex={0}
          aria-labelledby={ProrationNoteId}
          aria-describedby={ProrationTooltipId}
        />
      </Tooltip>
    </div>
  );
};

ProrationNote.propTypes = {
  allocationPeriod: PropTypes.oneOf([
    ALLOCATION_PERIOD.MONTH,
    ALLOCATION_PERIOD.WEEK
  ]).isRequired
};

export default ProrationNote;
